import {
  NODE_HEIGHT,
  NODE_WIDTH,
} from './assessment-attack-graph-layout.constants';
import { ATTACK_PATH_COMPACT_MIN_ZOOM } from './attack-path-anchored-viewport.util';
import { ATTACK_PATH_EVENT_ROW_HEIGHT } from './build-attack-path-event-graph.util';

export const ATTACK_PATH_OVERVIEW_WIDGET_MIN_HEIGHT = 200;
export const ATTACK_PATH_OVERVIEW_WIDGET_MAX_HEIGHT = 560;
export const ATTACK_PATH_OVERVIEW_CONTENT_HEIGHT_THRESHOLD =
  ATTACK_PATH_EVENT_ROW_HEIGHT;

export type AttackPathOverviewBoundNode = {
  position: { x: number; y: number };
  style?: { width?: number | string; height?: number | string };
};

const nodeHeight = (node: AttackPathOverviewBoundNode): number => {
  const styledHeight = node.style?.height;

  return typeof styledHeight === 'number' ? styledHeight : NODE_HEIGHT;
};

const nodeWidth = (node: AttackPathOverviewBoundNode): number => {
  const styledWidth = node.style?.width;

  return typeof styledWidth === 'number' ? styledWidth : NODE_WIDTH;
};

export const resolveAssessmentAttackGraphContentHeight = (
  nodes: ReadonlyArray<AttackPathOverviewBoundNode>,
): number => {
  if (nodes.length === 0) {
    return 0;
  }

  const bounds = nodes.reduce(
    (current, node) => {
      const height = nodeHeight(node);

      return {
        minY: Math.min(current.minY, node.position.y),
        maxY: Math.max(current.maxY, node.position.y + height),
      };
    },
    { minY: Number.POSITIVE_INFINITY, maxY: Number.NEGATIVE_INFINITY },
  );

  return Math.max(0, bounds.maxY - bounds.minY);
};

export const resolveAssessmentAttackGraphContentWidth = (
  nodes: ReadonlyArray<AttackPathOverviewBoundNode>,
): number => {
  if (nodes.length === 0) {
    return 0;
  }

  const bounds = nodes.reduce(
    (current, node) => {
      const width = nodeWidth(node);

      return {
        minX: Math.min(current.minX, node.position.x),
        maxX: Math.max(current.maxX, node.position.x + width),
      };
    },
    { minX: Number.POSITIVE_INFINITY, maxX: Number.NEGATIVE_INFINITY },
  );

  return Math.max(0, bounds.maxX - bounds.minX);
};

export const resolveAttackPathOverviewWidgetHeight = ({
  nodes,
  minHeight = ATTACK_PATH_OVERVIEW_WIDGET_MIN_HEIGHT,
  maxHeight = ATTACK_PATH_OVERVIEW_WIDGET_MAX_HEIGHT,
  growThreshold = ATTACK_PATH_OVERVIEW_CONTENT_HEIGHT_THRESHOLD,
}: {
  nodes: ReadonlyArray<AttackPathOverviewBoundNode>;
  minHeight?: number;
  maxHeight?: number;
  growThreshold?: number;
}): number => {
  const contentHeight = resolveAssessmentAttackGraphContentHeight(nodes);

  if (contentHeight <= growThreshold) {
    return minHeight;
  }

  const desiredHeight = Math.round(
    contentHeight * ATTACK_PATH_COMPACT_MIN_ZOOM,
  );

  return Math.min(maxHeight, Math.max(minHeight, desiredHeight));
};
